/**
 * Numbering follows the boxes on the two-page ICS 208HM-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS208HMCG.ts`.
 *
 * Page 1 runs boxes 1 to 9: the read-only header, the classification, the
 * material table, the physical effects, the incident needs, the PPE, and the
 * Prepared by. Page 2 runs 10 to 15: the hazard monitoring instruments, the
 * SOP, the decontamination procedures, the medical questions, the emergency
 * procedures, and the same Prepared by reprinted.
 *
 * The FEMA template behind this base id is the NIMS Site Safety and Control
 * Plan, a structurally different form, so none of this can be read off a
 * descriptor the way the 202's boxes are.
 */
export const ICS208HM_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  dateTimeOfReport: '3. Date / Time of Report:',
  hazmatClassification: '4. HAZMAT Classification:',
  material: '5. Product or Material Description:',
  additionalProductInfo: 'Additional Product Information:',
  potentialPhysicalEffects: '6. Potential Physical Effects:',
  incidentNeeds: '7. Does the incident require any of the following:',
  requiredPpe: '8. Required Personal Protective Equipment (PPE):',
  hazardMonitoring: '10. Hazard Monitoring:',
  sopAndSafeWorkPractices:
    '11. Standard Operating Procedures (SOP) and Safe Work Practices:',
  decontaminationProcedures: '12. Decontamination Procedures:',
  medical: '13. Medical:',
  emergencyProcedures: '14. Emergency Procedures:',
  /** One signature, printed twice: box 9 closes page 1 and box 15 closes page 2. */
  preparedBy: '9 & 15. Prepared by:',
} as const;

/** What each printed column of the box 5 table holds. */
export const ICS208HM_MATERIAL_COLUMNS = [
  { label: 'Material', field: 'materialName' },
  { label: 'Qty', field: 'quantity' },
  { label: 'Phys. State', field: 'physState' },
  { label: 'NIOSH #', field: 'niosh' },
  { label: 'Specific Gravity', field: 'specificGravity' },
  { label: 'pH', field: 'ph' },
  { label: 'IDLH', field: 'idlh' },
  { label: 'Flash Point', field: 'flashPoint' },
  { label: 'LEL', field: 'lel' },
  { label: 'UEL', field: 'uel' },
] as const;

/** The two questions box 13 asks, each a Yes/No pair on the printed sheet. */
export const ICS208HM_MEDICAL_QUESTIONS = [
  { key: 'medicalMonitoring', label: 'A. Medical Monitoring required?' },
  {
    key: 'medicalTreatment',
    label: 'B. Medical Treatment and Transport in place?',
  },
] as const;

/** The four instrument blanks box 10 prints, in printed order. */
export const ICS208HM_MONITORING_INSTRUMENTS = [
  { key: 'lelInstruments', label: 'LEL Instrument(s)' },
  { key: 'o2Instruments', label: 'O2 Instrument(s)' },
  { key: 'toxicityInstruments', label: 'Toxicity/PPM Instrument(s)' },
  { key: 'radiologicalInstruments', label: 'Radiological Instrument(s)' },
] as const;

export const ICS208HM_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

export const ICS208HM_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS208HM_DATE_TIME_OF_REPORT_HINT =
  'This box is read-only. It is filled from when this assessment was last written.';

/**
 * The printed sheet has five material rows. More than that is kept on the card
 * but only the first five reach the PDF, which is worth saying out loud.
 */
export const ICS208HM_MATERIAL_HINT =
  'The printed form has room for five materials. Any beyond the fifth are kept here but do not reach the exported PDF.';

/**
 * Three of the card's boxes are stored on the incident information record
 * rather than on the hazmat assessment, so they also print on other forms.
 */
export const ICS208HM_POTENTIAL_EFFECTS_HINT =
  'Shared with the incident information record, so the same selection prints wherever else the incident reports its potential hazards.';

export const ICS208HM_REQUIRED_PPE_HINT =
  'Shared with the incident information record, so the same selection prints wherever else the incident reports required PPE.';

export const ICS208HM_MEDICAL_HINT =
  'Both questions print as a Yes / No pair. Leaving one unanswered prints No, as the form has no third box.';
